import React, { useState, useEffect } from 'react';
import { Target, BarChart3 } from 'lucide-react';

/**
 * FunnelSettingsPanel — replaces the right sidebar when no block is selected
 * and the site is a funnel. Shows funnel-level settings for the current step.
 */

const LABEL = "block text-xs font-medium text-stone-600 mb-1";
const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";

const CONVERSION_GOALS = [
  { value: 'form_submission', label: 'Form Submission' },
  { value: 'button_click', label: 'Button Click' },
  { value: 'page_view', label: 'Page View' },
  { value: 'scroll_depth', label: 'Scroll Depth' },
  { value: 'custom', label: 'Custom Event' },
];

const STEP_TYPES = [
  { value: 'funnel_step', label: 'Funnel Step' },
  { value: 'thank_you', label: 'Thank You' },
  { value: 'upsell', label: 'Upsell' },
];

export default function FunnelSettingsPanel({ page, siteId, allPages = [], onUpdate }) {
  const [name, setName] = useState(page?.name || '');
  const config = page?.funnel_config || {};

  useEffect(() => {
    setName(page?.name || '');
  }, [page?.page_id, page?.name]);

  if (!page) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-stone-400 px-6">
        <Target className="w-8 h-8 mb-3 text-stone-300" />
        <p className="text-sm font-medium text-stone-500">No step selected</p>
      </div>
    );
  }

  const handleNameBlur = () => {
    if (name.trim() && name !== page.name) {
      onUpdate(page.page_id, { name: name.trim() });
    }
  };

  const updateConfig = (key, value) => {
    const newConfig = { ...config, [key]: value };
    onUpdate(page.page_id, { funnel_config: newConfig });
  };

  const handleStepType = (type) => {
    onUpdate(page.page_id, { page_type: type });
  };

  // Determine redirect options: other pages in the funnel
  const otherPages = allPages
    .filter(p => p.page_id !== page.page_id)
    .sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0));

  // Find "next step" based on funnel_order
  const currentOrder = page.funnel_order ?? 0;
  const nextStep = allPages
    .filter(p => (p.funnel_order ?? 0) > currentOrder)
    .sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0))[0];

  const redirectValue = config.redirect_on_conversion || 'next_step';
  const isCustomRedirect = redirectValue !== 'next_step' &&
    !otherPages.some(p => p.page_id === redirectValue);

  // Placeholder analytics
  const analytics = {
    views: page.views ?? 0,
    conversions: page.conversions ?? 0,
    rate: page.views ? ((page.conversions ?? 0) / page.views * 100).toFixed(1) : '0.0',
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-shrink-0">
        <Target className="w-4 h-4 text-stone-500" />
        <span
          className="text-sm font-semibold text-stone-800"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Funnel Step Settings
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Step Name */}
        <div>
          <label className={LABEL}>Step Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameBlur(); }}
            className={INPUT}
          />
        </div>

        {/* Step Type */}
        <div>
          <label className={LABEL}>Step Type</label>
          <div className="space-y-1.5">
            {STEP_TYPES.map(({ value, label }) => (
              <label
                key={value}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  page.page_type === value
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <input
                  type="radio"
                  name="step_type"
                  checked={page.page_type === value}
                  onChange={() => handleStepType(value)}
                  className="text-stone-900 focus:ring-stone-900"
                />
                <span className="text-xs font-medium text-stone-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Conversion Goal */}
        <div>
          <label className={LABEL}>Conversion Goal</label>
          <select
            value={config.conversion_goal || 'form_submission'}
            onChange={(e) => updateConfig('conversion_goal', e.target.value)}
            className={INPUT}
          >
            {CONVERSION_GOALS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {config.conversion_goal === 'button_click' && (
            <div className="mt-2">
              <label className={LABEL}>Goal Element ID</label>
              <input
                value={config.goal_element_id || ''}
                onChange={(e) => updateConfig('goal_element_id', e.target.value)}
                placeholder="e.g. cta-button"
                className={INPUT}
              />
            </div>
          )}
        </div>

        {/* Redirect After Conversion */}
        <div>
          <label className={LABEL}>Redirect After Conversion</label>
          <select
            value={isCustomRedirect ? 'custom_url' : redirectValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'custom_url') {
                updateConfig('redirect_on_conversion', '');
              } else {
                updateConfig('redirect_on_conversion', val);
              }
            }}
            className={INPUT}
          >
            <option value="next_step">
              Next step{nextStep ? ` (${nextStep.name})` : ''}
            </option>
            {otherPages.map(p => (
              <option key={p.page_id} value={p.page_id}>
                Step {(p.funnel_order ?? 0) + 1}: {p.name}
              </option>
            ))}
            <option value="custom_url">Custom URL</option>
          </select>

          {isCustomRedirect && (
            <div className="mt-2">
              <label className={LABEL}>Custom URL</label>
              <input
                value={config.redirect_on_conversion || ''}
                onChange={(e) => updateConfig('redirect_on_conversion', e.target.value)}
                placeholder="https://..."
                className={INPUT}
              />
            </div>
          )}
        </div>

        {/* Skip if converted */}
        <label className="flex items-center gap-2.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={config.skip_if_converted || false}
            onChange={(e) => updateConfig('skip_if_converted', e.target.checked)}
            className="rounded border-stone-300 text-stone-900 focus:ring-stone-900"
          />
          <span className="text-xs text-stone-700">Skip if already converted</span>
        </label>

        {/* Analytics */}
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              Step Analytics
            </span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-stone-50 rounded-lg">
              <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {analytics.views.toLocaleString()}
              </p>
              <p className="text-[10px] text-stone-500 font-medium mt-0.5">Views</p>
            </div>
            <div className="text-center p-3 bg-stone-50 rounded-lg">
              <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {analytics.conversions.toLocaleString()}
              </p>
              <p className="text-[10px] text-stone-500 font-medium mt-0.5">Conversions</p>
            </div>
            <div className="text-center p-3 bg-stone-50 rounded-lg">
              <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {analytics.rate}%
              </p>
              <p className="text-[10px] text-stone-500 font-medium mt-0.5">Conv. Rate</p>
            </div>
          </div>

          <p className="text-[10px] text-stone-400 mt-2 text-center">
            Analytics will be populated once the funnel is published and receiving traffic.
          </p>
        </div>
      </div>
    </div>
  );
}
